// ─────────────────────────────────────────────────────────────────────────────
// Estimate pricing engine — the single computation used by BOTH the /estimate
// page (display) and /api/estimate (authoritative recompute). The API never
// trusts a client-supplied total; it recomputes from the selection here.
// Pricing values come from offers/content.ts so quotes can never diverge from
// the published pricing page.
// ─────────────────────────────────────────────────────────────────────────────
import { annualSetup, genericContent, type Currency, type Package } from "@/app/(frontend)/offers/content"

export type PackageKey = "chatbot" | "holistic"
export type Billing = "monthly" | "annual" | "special"

export const PACKAGE_KEYS: PackageKey[] = ["chatbot", "holistic"]
export const BILLING_OPTIONS: Billing[] = ["monthly", "annual", "special"]

const PACKAGE_INDEX: Record<PackageKey, number> = { chatbot: 0, holistic: 1 }

export function getPackage(key: PackageKey): Package {
  return genericContent.packages[PACKAGE_INDEX[key]]
}

export type QuoteLine = {
  label: string
  detail: string
  unitAmount: number
  qty: number
  amount: number
}

export type Quote = {
  packageKey: PackageKey
  packageName: string
  billing: Billing
  currency: Currency
  lines: QuoteLine[]
  /** Total over the first 12 months (there is no minimum term). */
  total: number
  /** First payment: setup + first month (monthly), full year (annual), upfront + first month (special). */
  dueToday: number
  /** Ongoing monthly amount after the first payment (0 for annual). */
  monthlyOngoing: number
  /** Full Pipeline: the monthly fee starts on launch day, so dueToday is the setup fee alone. */
  monthlyStartsAtLaunch: boolean
  termMonths: number
  notes: string[]
}

/** True when the ZAR opening special applies to this package + currency. */
export function specialAvailable(key: PackageKey, currency: Currency): boolean {
  const special = getPackage(key).openingSpecial
  return !!special && special.currency === currency
}

export function computeQuote(key: PackageKey, billing: Billing, currency: Currency): Quote {
  const pkg = getPackage(key)
  const price = pkg.price[currency]
  const setup = pkg.setup[currency]

  if (billing === "special") {
    const special = pkg.openingSpecial
    if (!special || special.currency !== currency) {
      throw new Error(`No opening special for ${key} in ${currency}`)
    }
    const lines: QuoteLine[] = [
      { label: `${pkg.name} — ${special.label}`, detail: special.badge, unitAmount: special.upfront, qty: 1, amount: special.upfront },
      { label: "Monthly plan", detail: `${special.months}-month term (${special.regularNote})`, unitAmount: special.monthly, qty: special.months, amount: special.monthly * special.months },
    ]
    return {
      packageKey: key,
      packageName: pkg.name,
      billing,
      currency,
      lines,
      total: special.upfront + special.monthly * special.months,
      dueToday: special.upfront + special.monthly,
      monthlyOngoing: special.monthly,
      monthlyStartsAtLaunch: false,
      termMonths: special.months,
      notes: [
        `${special.label} — ${special.badge}.`,
        `${special.months}-month term, then month-to-month.`,
      ],
    }
  }

  if (billing === "annual") {
    // Annual = 10 × monthly (2 months free) + half the setup fee, all paid upfront.
    const halfSetup = annualSetup(setup)
    const lines: QuoteLine[] = [
      { label: "One-time setup (half price on annual)", detail: "Build and configuration of your system", unitAmount: halfSetup, qty: 1, amount: halfSetup },
      { label: `${pkg.name} — annual plan`, detail: "12 months for the price of 10", unitAmount: price, qty: 10, amount: price * 10 },
    ]
    return {
      packageKey: key,
      packageName: pkg.name,
      billing,
      currency,
      lines,
      total: halfSetup + price * 10,
      dueToday: halfSetup + price * 10,
      monthlyOngoing: 0,
      monthlyStartsAtLaunch: !!pkg.monthlyStartsAtLaunch,
      termMonths: 12,
      notes: [
        "Full year paid upfront: 2 months free and half the setup fee.",
        pkg.monthlyStartsAtLaunch ? "Your year starts on the day your system goes live." : "",
        "No minimum term: cancel any time and we refund the months you haven't used, after charging the months you used at the monthly price.",
        "Don't love your homepage design after 2 rounds of changes? You get every cent back.",
      ].filter(Boolean),
    }
  }

  // Monthly: one-time setup + the first 12 months billed monthly (no minimum term).
  // Full Pipeline: the monthly fee starts on launch day, so nothing but setup is due
  // today. The "(starts at launch)" label is what ghl-invoice.ts keys on to hold the
  // monthly schedule back until launch, so keep the wording in step with it.
  const atLaunch = !!pkg.monthlyStartsAtLaunch
  const lines: QuoteLine[] = [
    { label: "One-time setup", detail: "Build and configuration of your system", unitAmount: setup, qty: 1, amount: setup },
    atLaunch
      ? { label: `${pkg.name} — monthly plan (starts at launch)`, detail: "Starts on the day your system goes live · first 12 months, billed monthly · cancel any time", unitAmount: price, qty: 12, amount: price * 12 }
      : { label: `${pkg.name} — monthly plan`, detail: "First 12 months, billed monthly · cancel any time", unitAmount: price, qty: 12, amount: price * 12 },
  ]
  return {
    packageKey: key,
    packageName: pkg.name,
    billing,
    currency,
    lines,
    total: setup + price * 12,
    dueToday: atLaunch ? setup : setup + price,
    monthlyOngoing: price,
    monthlyStartsAtLaunch: atLaunch,
    termMonths: 12,
    notes: [
      atLaunch
        ? "Your monthly fee starts on the day your system goes live, not today."
        : "Your site goes live 2 weeks after you send us your text, photos and logo, or your next month is free.",
      "No minimum term: cancel any time and your plan stops at the end of the month you've paid for.",
      "Don't love your homepage design after 2 rounds of changes? You get every cent back.",
      "Pay annually and you get 2 months free and half off the setup fee.",
    ],
  }
}
